import { useState, useRef, useEffect } from 'react';
import { Bell, Menu, X, Search, LogOut, Check, UserPlus, Wrench, FileText, DollarSign, Moon, Sun, Calendar } from 'lucide-react';
import { theme } from '../../lib/theme';
import { useDarkMode } from '../../hooks/useDarkMode';

export default function TopBar({
  sidebarExpanded, setSidebarExpanded, onSignOut,
  notifications = [], onMarkRead,
  searchQuery = '', onSearch, searchResults, onSearchSelect,
  user, userRole, onNavigate,
}) {
  const [showNotifs, setShowNotifs] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [isDark, toggleDark] = useDarkMode();
  const notifRef = useRef(null);
  const searchRef = useRef(null);

  const now = new Date();
  const hr = now.getHours();
  const greeting = hr < 12 ? 'Good morning' : hr < 17 ? 'Good afternoon' : 'Good evening';
  const dateStr = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const firstName = user?.user_metadata?.full_name?.split(' ')[0] || 'there';

  const unreadCount = notifications.filter(n => !n.read_at).length;

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifs(false);
      if (searchRef.current && !searchRef.current.contains(e.target)) { setShowSearch(false); onSearch?.(''); }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onSearch]);

  const formatTime = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  const getNotifIcon = (type) => {
    if (type?.includes('lead')) return UserPlus;
    if (type?.includes('maintenance')) return Wrench;
    if (type?.includes('deal') || type?.includes('transaction')) return FileText;
    if (type?.includes('financial')) return DollarSign;
    return Bell;
  };

  return (
    <div style={{
      height: 56,
      backgroundColor: theme.colors.card,
      borderBottom: `1px solid ${theme.colors.border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 20px',
      position: 'sticky', top: 0, zIndex: 50,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <button
          onClick={() => setSidebarExpanded(!sidebarExpanded)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.textLight, display: 'flex', padding: 4 }}
        >
          {sidebarExpanded ? <X size={20} /> : <Menu size={20} />}
        </button>
        {!showSearch && (
          <div>
            <span style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text }}>{greeting}, {firstName}</span>
            <span style={{ fontSize: 12, color: theme.colors.textLight, marginLeft: 10 }}>{dateStr}</span>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {/* Search */}
        <div ref={searchRef} data-tour="search" style={{ position: 'relative' }}>
          {showSearch ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                autoFocus
                type="text"
                placeholder="Search leads, properties..."
                value={searchQuery}
                onChange={(e) => onSearch?.(e.target.value)}
                style={{
                  width: 220, padding: '6px 12px', borderRadius: 8,
                  border: `1px solid ${theme.colors.border}`, fontSize: 13,
                  outline: 'none', fontFamily: 'inherit',
                }}
              />
              <button onClick={() => { setShowSearch(false); onSearch?.(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.textLight, display: 'flex', padding: 2 }}>
                <X size={16} />
              </button>
            </div>
          ) : (
            <button onClick={() => setShowSearch(true)} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: theme.colors.textLight, display: 'flex', padding: 4,
            }}>
              <Search size={18} />
            </button>
          )}

          {/* Search Results Dropdown */}
          {searchResults && (searchResults.leads?.length > 0) && (
            <div style={{
              position: 'absolute', top: '100%', right: 0, marginTop: 8,
              width: 300, background: theme.colors.card,
              borderRadius: theme.radius.lg, boxShadow: theme.shadow.lg,
              border: `1px solid ${theme.colors.border}`, overflow: 'hidden', zIndex: 200,
            }}>
              {searchResults.leads?.length > 0 && (
                <div>
                  <div style={{ padding: '8px 14px', fontSize: 10, fontWeight: 700, color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: 1 }}>Leads</div>
                  {searchResults.leads.map(r => (
                    <div key={r.id} onClick={() => onSearchSelect?.(r)} style={{
                      padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${theme.colors.border}`,
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = theme.colors.bg}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text }}>{r.title}</div>
                      <div style={{ fontSize: 11, color: theme.colors.textLight }}>{r.subtitle}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Dark mode toggle */}
        <button
          onClick={toggleDark}
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: theme.colors.textLight, display: 'flex', padding: 4,
          }}
        >
          {isDark ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        {/* Calendar shortcut */}
        {onNavigate && (
          <button
            onClick={() => onNavigate('leads', 'calendar')}
            title="Calendar"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: theme.colors.textLight, display: 'flex', padding: 4,
            }}
          >
            <Calendar size={18} />
          </button>
        )}

        {/* Notifications */}
        <div ref={notifRef} data-tour="notifications" style={{ position: 'relative' }}>
          <button
            onClick={() => setShowNotifs(!showNotifs)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: 4, position: 'relative' }}
          >
            <Bell size={18} color={theme.colors.textLight} />
            {unreadCount > 0 && (
              <div style={{
                position: 'absolute', top: -2, right: -2,
                width: 16, height: 16, borderRadius: 8,
                backgroundColor: theme.colors.danger,
                color: 'white', fontSize: 9, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>{unreadCount > 9 ? '9+' : unreadCount}</div>
            )}
          </button>

          {/* Notification Dropdown */}
          {showNotifs && (
            <div style={{
              position: 'absolute', top: '100%', right: 0, marginTop: 8,
              width: 340, maxHeight: 420, overflowY: 'auto',
              background: theme.colors.card,
              borderRadius: theme.radius.lg, boxShadow: theme.shadow.lg,
              border: `1px solid ${theme.colors.border}`, zIndex: 200,
            }}>
              <div style={{
                padding: '12px 16px', borderBottom: `1px solid ${theme.colors.border}`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span style={{ fontWeight: 700, fontSize: 14, color: theme.colors.text }}>Notifications</span>
                {unreadCount > 0 && (
                  <span style={{ fontSize: 11, color: theme.colors.primary, fontWeight: 600 }}>
                    {unreadCount} unread
                  </span>
                )}
              </div>

              {notifications.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: theme.colors.textLight, fontSize: 13 }}>
                  No notifications yet
                </div>
              ) : (
                notifications.slice(0, 15).map(n => {
                  const Icon = getNotifIcon(n.notification_type);
                  const isUnread = !n.read_at;
                  return (
                    <div
                      key={n.id}
                      onClick={() => { if (isUnread) onMarkRead?.(n.id); }}
                      style={{
                        padding: '12px 16px',
                        borderBottom: `1px solid ${theme.colors.border}`,
                        backgroundColor: isUnread ? `${theme.colors.primary}06` : 'transparent',
                        cursor: isUnread ? 'pointer' : 'default',
                        display: 'flex', gap: 10,
                      }}
                    >
                      <div style={{
                        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                        backgroundColor: isUnread ? `${theme.colors.primary}15` : theme.colors.bg,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Icon size={14} color={isUnread ? theme.colors.primary : theme.colors.textLight} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 13, fontWeight: isUnread ? 600 : 400, color: theme.colors.text,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>{n.title}</div>
                        {n.body && <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.body}</div>}
                        <div style={{ fontSize: 10, color: theme.colors.textLight, marginTop: 3 }}>{formatTime(n.created_at)}</div>
                      </div>
                      {isUnread && (
                        <div style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: theme.colors.primary, flexShrink: 0, marginTop: 4 }} />
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Sign out */}
        <button
          onClick={onSignOut}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: theme.colors.textLight, display: 'flex', padding: 4,
          }}
          title="Sign out"
        >
          <LogOut size={18} />
        </button>
      </div>
    </div>
  );
}
